<script setup>
import { useSettingsStore } from "@stores/settings";
import { useAuthStore } from "@stores/auth";
import { useDepositStore } from "@stores/deposit";
import { useToPlayStore } from "@stores/to-play";
import { useGame } from "@composables/base/useGame";
import { useLegitimuzStatusStore } from '@stores/legitimuz-status';
import { useUiStore } from '@stores/ui';

// Stores
const authStore = useAuthStore();
const settingsStore = useSettingsStore();
const depositStore = useDepositStore();
const toPlayStore = useToPlayStore();
const legitimuzStore = useLegitimuzStatusStore();
const uiStore = useUiStore();

definePageMeta({
  middleware: ['refresh-ip', 'require-verification']
});

const {
  game,
  demo,
  isLoading,
  isGameLoaded,
  fetchGameData
} = useGame();

const settings = settingsStore.settings;

const props = defineProps({
  gameId: {
    type: String
  }
});

const { id } = useRoute().params;
let category = ref(null);
let needsVerification = ref(false);

const logoSrc = settings.general_logo;

watch(demo, async (newValue) => {
  let value = newValue ? 1 : 0;
  try {
    game.value = await fetchGameData(value, id);
  } catch (error) {
    console.error("Error fetching game data:", error);
  }
});

let isFullscreenActivated = ref(false);

const toggleFullscreen = () => {
  if (import.meta.client) {
    if (!document.fullscreenElement) {
      console.log("requesting full screen");
      document.getElementById("casino-table")?.requestFullscreen();
      isFullscreenActivated.value = true;
    } else if (document.exitFullscreen) {
      console.log("exiting full screen");
      document.exitFullscreen();
      isFullscreenActivated.value = false;
    }
  }
};

watch(
  () => authStore.user,
  async (newValue) => {
    if (newValue) {
      try {
        game.value = await fetchGameData(0, id);
      } catch (error) {
        console.error("Error fetching game data after user update:", error);
      }
    }
  }
);

async function openGameModal() {
  try {
    game.value = await fetchGameData(0, id);
    toPlayStore.toggleGameModal(true);
  } catch (error) {
    console.error("Error opening game modal:", error);
  }
}

function share() {
  if (import.meta.client) {
    if (navigator.share) {
      const data = {
        title: game.value?.game?.name || "Game",
        text: game.value?.game?.provider?.name || "Provider",
        url: window.location.href
      };

      navigator
        .share(data)
        .then(() => {
          console.log("Conteúdo compartilhado com sucesso.");
        })
        .catch((error) => {
          console.error("Ocorreu um erro ao compartilhar: ", error);
        });
    } else {
      console.log("A API Web Share não é suportada neste navegador.");
    }
  }
}

const checkVerificationStatus = async () => {
  if (authStore.user) {
    const isApproved = await legitimuzStore.checkStatus(true);
    needsVerification.value = !isApproved;
    
    if (needsVerification.value) {
      uiStore.showVerificationModal = true;
    }
  }
};

onMounted(async () => {
  isFullscreenActivated.value = !!document.fullscreenElement;
  window.addEventListener("message", function (event) {
    if (event.data.type === "RGS-home") {
      return navigateTo("/");
    }
  });

  await checkVerificationStatus();
  
  if (isMobileDevice()) {
    toPlayStore.toggleGameModal();
  }
});

onBeforeMount(async () => {
  toPlayStore.state.isMenuOpen = false;
  toPlayStore.state.isGameModalOpen = false;

  try {
    game.value = await fetchGameData(0, id);
    
    if (game.value?.game?.categories?.length > 0) {
      category.value = game.value.game.categories[0]?.name;
      getType(category.value);
    } else {
      category.value = "1";
    }
  } catch (error) {
    console.error("Error in onBeforeMount:", error);
    category.value = "1";
  }
});

function getType(type) {
  if (type === "Slots") {
    category.value = "1";
  } else {
    category.value = "2";
  }
}
</script>

<template>
  <client-only>
    <LegitimuzElements />
    <div v-if="(!game?.game && !isLoading) || needsVerification">
      <div
        class="flex flex-col w-full h-full items-center justify-center p-6 bg-black border border-separator rounded-md md:h-[60vh] xs:h-[30vh] mb-5">
        <Icon name="ic:round-warning" size="4em" />
        <p class="font-bold" v-if="needsVerification">Verificação necessária para acessar este conteúdo, caso já esteja verificado recarregue a página</p>
        <p class="font-bold" v-else>{{ game?.message ? game.message : "Jogo não encontrado" }}</p>
      </div>
      <div>
        <PlayRelated :category="category" />
      </div>
    </div>
    <section v-else class="flex flex-col gap-6">
      <div v-if="
        isMobileDevice() && toPlayStore.state.isGameModalOpen && game.link
      "
        class="fixed bottom-0 left-0 right-0 top-0 z-[52] flex flex-row items-center justify-center overflow-y-hidden bg-black/50 text-white">
        <div
          class="relative flex h-full w-full flex-col overflow-y-auto bg-secondary xl:h-auto xl:max-w-sm xl:rounded-md">
          <header class="top-2 z-50 flex h-[45px] cursor-pointer justify-between bg-brand-navbar p-2">
            
            <div class="flex items-center gap-4">
              <BaseButton size="xs"
                class="h-fit gap-2 justify-self-start !px-2 !py-1 text-xs leading-none text-gray-400"
                @click="toPlayStore.toggleMenu()">
                <Icon name="fa6-solid:bars-staggered" size="14" />
              </BaseButton>

              <div class="flex">
                <BaseLogo class="max-h-[calc(40px-16px)] object-contain" />
              </div>
            </div>

            <div class="flex items-center gap-2 justify-self-end">
              <BaseButton variant="primary" size="xs" class="gap-2" @click="depositStore.toggleModal()"
                icon="fa6-solid:arrow-down">
                Depositar
              </BaseButton>
            </div>
          </header>
          <template v-if="game.link">
            <div class="absolute z-0 flex h-full w-full items-center justify-center" v-if="!isGameLoaded">
              <Icon class="animate-spin text-brand" name="svg-spinners:180-ring-with-bg" size=".5em" />
            </div>
            <template v-if="!isLoading">
              <iframe v-if="game.game.provider.aggregator == 'pgsoft'" :srcdoc="game.link"
                class="relative z-10 h-full w-full" @load="isGameLoaded = true"></iframe>
              <iframe v-else :src="game.link" class="relative z-10 h-full w-full" @load="isGameLoaded = true"></iframe>
            </template>
          </template>
          <template v-else>
            <img class="h-full w-full" :src="game.image" alt="Game image" />

            <div class="flex items-center justify-center">
              <div class="bg- absolute left-0 top-0 h-full w-full blur-sm backdrop-brightness-50"></div>

              <div
                class="absolute bottom-0 left-0 right-0 top-0 m-auto flex h-96 w-96 flex-col items-center justify-center blur-none">
                <svg class="h-16 fill-white" viewBox="0 0 448 512">
                  <path
                    d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z" />
                </svg>

                <p class="font-bold">Você precisa entrar para jogar.</p>

                <div>
                  <div class="flex flex-row items-center justify-center py-2">
                    <div @click="authStore.modalOpen('login')" :class="[
                      authStore.currentField === 'login' ? 'text-white' : '',
                    ]"
                      class="flex cursor-pointer items-center rounded-md bg-brand-primary px-3 py-2 text-sm font-bold text-bzMenuText hover:text-white">
                      Entrar
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div v-if="isMobileDevice() && settings.casino_enabled"
        class="w-full rounded-md border border-brand-separator bg-brand-card">
        <div :style="`background-image: url(${game?.game?.image})`"
          class="relative flex h-32 w-full justify-end rounded-t-md bg-opacity-50 bg-cover bg-no-repeat p-4 after:absolute after:bottom-0 after:left-0 after:z-10 after:flex after:h-20 after:w-full after:bg-gradient-to-b after:from-transparent after:to-secondary after:content-['']">
          <BaseButton size="xs" @click="share()" id="share" icon="fa6-solid:share-nodes"
            class="hover:text-brand-primaryactive:bg-darken-brand-primary z-25 self-start bg-black/60 text-gray-300 hover:bg-darken-brand-primary active:text-brand">
            Compartilhar
          </BaseButton>
        </div>

        <div class="px-6 py-5">
          <div class="flex flex-row items-center justify-between">
            <div>
              <p class="text-sm font-bold text-white">
                {{ game?.game?.name }}
              </p>
              <p class="text-xs text-gray-400">
                {{ game?.game?.provider?.name }}
              </p>
            </div>
            <!--            <BaseFavoriteToggle :game="game?.game" :icon="'heart'" />-->
          </div>

          <div class="my-5 flex items-center gap-4" v-if="false">
            <button>
              <svg class="h-6 fill-gray-500" viewBox="0 0 512 512">
                <path
                  d="M313.4 32.9c26 5.2 42.9 30.5 37.7 56.5l-2.3 11.4c-5.3 26.7-15.1 52.1-28.8 75.2H464c26.5 0 48 21.5 48 48c0 18.5-10.5 34.6-25.9 42.6C497 275.4 504 288.9 504 304c0 23.4-16.8 42.9-38.9 47.1c4.4 7.3 6.9 15.8 6.9 24.9c0 21.3-13.9 39.4-33.1 45.6c.7 3.3 1.1 6.8 1.1 10.4c0 26.5-21.5 48-48 48H294.5c-19 0-37.5-5.6-53.3-16.1l-38.5-25.7C176 420.4 160 390.4 160 358.3V320 272 247.1c0-29.2 13.3-56.7 36-75l7.4-5.9c26.5-21.2 44.6-51 51.2-84.2l2.3-11.4c5.2-26 30.5-42.9 56.5-37.7zM32 192H96c17.7 0 32 14.3 32 32V448c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V224c0-17.7 14.3-32 32-32z" />
              </svg>
            </button>
            <p class="text-base font-bold text-gray-400">9999</p>
            <button>
              <svg class="h-6 scale-x-[-1] fill-gray-500" viewBox="0 0 512 512">
                <path
                  d="M313.4 479.1c26-5.2 42.9-30.5 37.7-56.5l-2.3-11.4c-5.3-26.7-15.1-52.1-28.8-75.2H464c26.5 0 48-21.5 48-48c0-18.5-10.5-34.6-25.9-42.6C497 236.6 504 223.1 504 208c0-23.4-16.8-42.9-38.9-47.1c4.4-7.3 6.9-15.8 6.9-24.9c0-21.3-13.9-39.4-33.1-45.6c.7-3.3 1.1-6.8 1.1-10.4c0-26.5-21.5-48-48-48H294.5c-19 0-37.5 5.6-53.3 16.1L202.7 73.8C176 91.6 160 121.6 160 153.7V192v48 24.9c0 29.2 13.3 56.7 36 75l7.4 5.9c26.5 21.2 44.6 51 51.2 84.2l2.3 11.4c5.2 26 30.5 42.9 56.5 37.7zM32 384H96c17.7 0 32-14.3 32-32V128c0-17.7-14.3-32-32-32H32C14.3 96 0 110.3 0 128V352c0 17.7 14.3 32 32 32z" />
              </svg>
            </button>
          </div>

          <div class="mt-5 flex h-12 w-full items-center gap-4">
            <BaseButton class="flex-1" variant="secondary" v-if="game?.game?.demo" @click="
              demo = 1;
            toPlayStore.toggleGameModal();
            " icon="fa6-solid:play">
              Demo
            </BaseButton>

            <BaseButton variant="primary" v-if="authStore.isLoggedIn" @click="openGameModal()" icon="fa6-solid:play">
              Jogar Agora
            </BaseButton>

            <BaseButton class="flex-1" @click="authStore.modalOpen('login')" variant="primary" v-else>
              Entrar
            </BaseButton>
          </div>
        </div>
      </div>

      <!-- Desktop Game -->
      <div v-if="!isMobileDevice() && settings.casino_enabled"
        class="flex w-full overflow-hidden rounded-md border border-separator md:block z-50"
        id="casino-table">
        <div class="h-full w-full">
          <!-- Game iFrame -->
          <div class="relative aspect-video w-full border-b border-separator" id="game">
            <!-- Imagem apenas pra preencher o espaço do jogo -->
            <template v-if="game.link">
              <div class="absolute z-10 flex h-full w-full items-center justify-center" v-if="!isGameLoaded">
                <Icon class="animate-spin text-4xl text-brand" name="svg-spinners:180-ring-with-bg" />
              </div>

              <template v-if="!isLoading">
                <iframe v-if="game.game.provider.aggregator == 'pgsoft'" :srcdoc="game.link"
                  class="relative z-20 h-full w-full"></iframe>
                <iframe v-else :src="game.link" class="relative z-20 h-full w-full"></iframe>
              </template>
            </template>
            <template v-else-if="!authStore.isLoggedIn">
              <div :class="`flex h-full items-center justify-center bg-cover bg-center`"
                :style="`background-image: url(${game?.game?.image})`">
                <div :class="`absolute left-0 top-0 h-full w-full blur-sm backdrop-brightness-50`"></div>

                <div
                  class="absolute bottom-0 left-0 right-0 top-0 m-auto flex h-100 w-100 flex-col items-center justify-center blur-none">
                  <svg class="h-16 fill-white" viewBox="0 0 448 512">
                    <path
                      d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z" />
                  </svg>

                  <p class="font-bold">Você precisa entrar para jogar.</p>

                  <div>
                    <div class="flex flex-row items-center justify-center py-2">
                      <div @click="authStore.modalOpen('login')" :class="[
                        authStore.currentField === 'login'
                          ? 'text-white'
                          : '',
                      ]"
                        class="flex cursor-pointer items-center rounded-md bg-brand-primary px-3 py-2 text-sm font-bold text-bzMenuText hover:text-white">
                        Entrar
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <!-- End Game iFrame -->

          <!-- Game header -->
          <div class="flex items-center justify-between bg-brand-card p-6" :class="{ 'flex-1': isFullscreenActivated }">
            <div class="text-xs">
              <h1 class="font-bold">{{ game?.game?.name }}</h1>
              <h2>{{ game?.game?.provider?.name }}</h2>
            </div>

            <div class="flex items-center gap-3">
              <div class="flex items-center gap-3 text-xs" v-if="game?.game?.demo">
                <p>Modo Real</p>
                <div>
                  <input v-model="demo" type="checkbox" value="1" id="onoff" class="peer hidden" />

                  <label for="onoff"
                    class="flex w-10 cursor-pointer items-center rounded-full border border-separator p-px transition-all peer-checked:justify-end">
                    <span class="h-4 w-4 rounded-full bg-brand-primary transition-all"></span>
                  </label>
                </div>
                <p>Modo Demo</p>
              </div>

              <div class="flex" v-if="game?.game">
                <BaseFavoriteToggle :game="game.game" :icon="'heart'" />
              </div>

              <button class="ml-3 fill-bzMenuText hover:scale-110 md:block" @click="toggleFullscreen">
                <svg class="h-5" viewBox="0 0 448 512">
                  <path
                    d="M32 32C14.3 32 0 46.3 0 64v96c0 17.7 14.3 32 32 32s32-14.3 32-32V96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V352zM320 32c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V64c0-17.7-14.3-32-32-32H320zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H320c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V352z" />
                </svg>
              </button>
            </div>
          </div>
          <!-- End Game header -->
        </div>
      </div>

      <div v-if="!settings.casino_enabled" class="flex">
        <h2 class="mx-auto mt-5 text-lg font-bold">Casino em manutenção</h2>
      </div>

      <div>
        <PlayRelated :category="category" />
      </div>
    </section>
  </client-only>

  <div class="mt-5" v-if="game?.game">
    <PlayDescription :game="game" />
  </div>

  <client-only>
    <PlayMenu />
  </client-only>
</template>